import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Flag, Crown, Layers, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { cn, formatDate } from '../../lib/utils';
import { PLACEHOLDER_IMAGE } from '../../lib/api';
import { ReportModal } from './ReportModal';
import { useLanguage } from '../../context/LanguageContext';

const RARITY_STYLES = {
    Common:   { bg: 'bg-gray-500/10',   border: 'border-gray-500/30',   text: 'text-gray-400'   },
    Uncommon: { bg: 'bg-green-500/10',  border: 'border-green-500/30',  text: 'text-green-400'  },
    Rare:     { bg: 'bg-blue-500/10',   border: 'border-blue-500/30',   text: 'text-blue-400'   },
    Epic:     { bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400' },
    Legendary:{ bg: 'bg-amber-500/10',  border: 'border-amber-500/30',  text: 'text-amber-400'  },
    Mythical: { bg: 'bg-red-500/10',    border: 'border-red-500/30',    text: 'text-red-400'    },
    Monarch:  { bg: 'bg-sky-500/10',    border: 'border-sky-500/30',    text: 'text-sky-400'    },
    Limited:  { bg: 'bg-pink-500/10',   border: 'border-pink-500/30',   text: 'text-pink-400'   },
    ULimited: { bg: 'bg-violet-900/10', border: 'border-violet-900/30', text: 'text-violet-400' },
};

// ─── Upgrade-Varianten-Block ─────────────────────────────────────────────────
// Zeigt eine einzelne Seltenheit mit all ihren Varianten.
// Jede Variante hat ein Dropdown zur Stufenauswahl.
const UpgradeRaritySection = ({ rarityEntry }) => {
    const style = RARITY_STYLES[rarityEntry.rarity] || { bg: 'bg-white/5', border: 'border-white/10', text: 'text-gray-400' };
    const variants = rarityEntry.variants || [];

    // Pro Variante: aktuell ausgewählte Stufe (Index)
    const [selectedLevels, setSelectedLevels] = useState(() =>
        variants.map(v => (v.levels?.length ? v.levels.length - 1 : 0))
    );

    if (variants.length === 0) return null;

    const setLevel = (variantIdx, levelIdx) => {
        setSelectedLevels(prev => {
            const next = [...prev];
            next[variantIdx] = levelIdx;
            return next;
        });
    };

    return (
        <div className={cn('rounded-xl border p-4 space-y-4', style.border, style.bg)}>
            {/* Seltenheits-Header */}
            <h3 className={cn('font-bold font-[\'Rajdhani\'] text-lg', style.text)}>
                {rarityEntry.rarity}
            </h3>

            {variants.map((variant, variantIdx) => {
                const levels = variant.levels || [];
                const selectedLevelIdx = Math.min(selectedLevels[variantIdx] ?? levels.length - 1, levels.length - 1);
                const currentLevel = levels[selectedLevelIdx];
                const isMaxed = levels.length > 0 && selectedLevelIdx === levels.length - 1;

                return (
                    <div
                        key={variantIdx}
                        className="rounded-lg bg-black/30 border border-white/10 p-4 space-y-3"
                    >
                        {/* Varianten-Header */}
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                                <Layers size={14} className="text-gray-500 shrink-0" />
                                <span className="font-semibold text-white text-sm">
                                    {variant.variant_name || `Variante ${variantIdx + 1}`}
                                </span>
                                {isMaxed && levels.length > 0 && (
                                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                                        <Crown size={10} />
                                        MAXED
                                    </span>
                                )}
                            </div>

                            {/* Stufen-Dropdown */}
                            {levels.length > 0 && (
                                <div className="relative">
                                    <select
                                        value={selectedLevelIdx}
                                        onChange={e => setLevel(variantIdx, Number(e.target.value))}
                                        className={cn(
                                            'appearance-none pl-3 pr-8 py-1.5 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 cursor-pointer transition-colors',
                                            'bg-black/50 text-white',
                                            isMaxed
                                                ? 'border-yellow-500/40 focus:ring-yellow-500/30'
                                                : 'border-green-500/30 focus:ring-green-500/20'
                                        )}
                                    >
                                        {levels.map((lv, li) => {
                                            const isFinalLevel = li === levels.length - 1;
                                            return (
                                                <option
                                                    key={li}
                                                    value={li}
                                                    className="bg-gray-900"
                                                >
                                                    Stufe {lv.level}{isFinalLevel ? ' ★ MAX' : ''}
                                                </option>
                                            );
                                        })}
                                    </select>
                                    <ChevronDown
                                        size={13}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Felder der ausgewählten Stufe */}
                        {currentLevel && currentLevel.fields && currentLevel.fields.length > 0 ? (
                            <div className={cn(
                                'grid gap-2 pt-2 border-t',
                                currentLevel.fields.length === 1
                                    ? 'grid-cols-1'
                                    : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
                                isMaxed ? 'border-yellow-500/20' : 'border-white/10'
                            )}>
                                {currentLevel.fields.map((field, fi) => (
                                    <div
                                        key={fi}
                                        className={cn(
                                            'p-3 rounded-lg',
                                            isMaxed
                                                ? 'bg-yellow-500/5 border border-yellow-500/15'
                                                : 'bg-white/5 border border-white/5'
                                        )}
                                    >
                                        <p className="text-xs text-gray-500 font-mono uppercase tracking-wider mb-1">
                                            {field.name}
                                        </p>
                                        <p className={cn(
                                            'font-mono font-medium',
                                            isMaxed ? 'text-yellow-300' : 'text-cyan-300'
                                        )}>
                                            {field.value}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : currentLevel ? (
                            <p className="text-xs text-gray-600 italic pt-1">
                                Keine Felder für diese Stufe definiert.
                            </p>
                        ) : (
                            <p className="text-xs text-gray-600 italic pt-1">
                                Keine Stufen für diese Variante definiert.
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ─── Haupt-Komponente ──────────────────────────────────────────────────────────
export const WikiDetail = ({ entry, category }) => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [selectedRankIndex, setSelectedRankIndex] = useState(0);

    const showReportButton = ['relics', 'accessories', 'gachas', 'upgrades'].includes(category);
    const isGacha    = category === 'gachas';
    const isUpgrade  = category === 'upgrades';
    const isRelic    = category === 'relics';
    const isAccessory = category === 'accessories';

    const ranks = entry?.ranks || [];
    const selectedRank = ranks[selectedRankIndex] || null;

    if (!entry) {
        return (
            <div className="flex flex-col items-center justify-center py-16">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                    <span className="text-3xl text-gray-600">🔍</span>
                </div>
                <h3 className="text-xl font-['Rajdhani'] text-gray-400 mb-2">
                    Eintrag nicht gefunden
                </h3>
            </div>
        );
    }

    const getRankStyle = (rankName) => {
        const name = rankName?.toLowerCase() || '';
        if (name.includes('legendary') || name.includes('legendär')) return 'rank-legendary';
        if (name.includes('epic') || name.includes('episch')) return 'rank-epic';
        if (name.includes('rare') || name.includes('selten')) return 'rank-rare';
        return 'rank-common';
    };

    const imageUrl = entry.image_url || PLACEHOLDER_IMAGE;
    const rarityStyle = entry.rarity ? RARITY_STYLES[entry.rarity] : null;
    const title = entry.title || entry.name;

    // Für Upgrades: Seltenheiten mit mindestens einer Variante
    const upgradeRarities = isUpgrade
        ? (entry.variants_by_rarity || []).filter(r => r.variants?.length > 0)
        : [];

    return (
        <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
            {/* Top Bar */}
            <div className="flex items-center justify-between">
                <Button
                    data-testid="back-button"
                    variant="ghost"
                    onClick={() => navigate(`/${category}`)}
                    className="text-gray-400 hover:text-white"
                >
                    <ArrowLeft size={18} className="mr-2" />
                    Zurück zu {category.charAt(0).toUpperCase() + category.slice(1)}
                </Button>

                <div className="flex items-center gap-2">
                    {showReportButton && (
                        <Button
                            data-testid="report-button"
                            variant="outline"
                            onClick={() => setReportModalOpen(true)}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
                        >
                            <Flag size={16} className="mr-2" />
                            {language === 'de' ? 'Problem melden' : 'Report issue'}
                        </Button>
                    )}
                </div>
            </div>

            {/* Header: Bild links, Infos rechts */}
            <div className="flex flex-col md:flex-row gap-8 items-start">
                {/* Bild */}
                <div className="flex-shrink-0">
                    <div className="inline-block overflow-hidden rounded-xl">
                        <img
                            src={imageUrl}
                            alt={title}
                            className="max-w-full h-auto object-contain rounded-lg"
                        />
                    </div>
                </div>

                {/* Infos */}
                <div className="flex-1 space-y-4">
                    <h1 className="text-4xl font-['Rajdhani'] font-bold text-white">
                        {title}
                    </h1>

                    <div className="flex items-center gap-3 flex-wrap">
                        {rarityStyle && (
                            <span className={cn(
                                'px-3 py-1 rounded text-sm font-mono uppercase tracking-wider border',
                                rarityStyle.bg, rarityStyle.border, rarityStyle.text
                            )}>
                                {entry.rarity}
                            </span>
                        )}
                        {entry.is_meta && (
                            <span className="px-3 py-1 rounded text-sm font-bold uppercase bg-orange-500 text-black">
                                META
                            </span>
                        )}
                        <span className="text-xs text-gray-500 font-mono">
                            {formatDate(entry.created_at)}
                        </span>
                    </div>

                    {/* Dynamic Fields (How to get) */}
                    {entry.dynamic_fields && entry.dynamic_fields.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-white/10">
                            <h2 className="text-lg font-['Rajdhani'] font-semibold text-cyan-400">
                                How to get?
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                                {entry.dynamic_fields.map((field, idx) => (
                                    <div
                                        key={idx}
                                        className="p-3 rounded-lg bg-white/5 border border-white/10"
                                    >
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-mono mb-1">
                                            {field.name}
                                        </p>
                                        <p className="text-base text-cyan-300 font-mono font-medium">
                                            {field.value}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Attributes (Accessories) */}
                    {entry.attributes && entry.attributes.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-white/10">
                            <h2 className="text-lg font-['Rajdhani'] font-semibold text-amber-400">
                                Attributes
                            </h2>
                            <div className="grid grid-cols-2 gap-3">
                                {entry.attributes.map((field, idx) => (
                                    <div
                                        key={idx}
                                        className="p-3 rounded-lg bg-white/5 border border-amber-500/10"
                                    >
                                        <p className="text-xs text-gray-500 uppercase tracking-wider font-mono mb-1">
                                            {field.name}
                                        </p>
                                        <p className="text-base text-amber-300 font-mono font-medium">
                                            {field.value}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Rank Dropdown (Relics) */}
                    {isRelic && ranks.length > 0 && (
                        <div className="space-y-3 pt-4 border-t border-white/10">
                            <h2 className="text-lg font-['Rajdhani'] font-semibold text-amber-400">
                                Ranks
                            </h2>
                            <select
                                data-testid="rank-selector"
                                value={selectedRankIndex}
                                onChange={(e) => setSelectedRankIndex(Number(e.target.value))}
                                className="w-full p-3 rounded-lg bg-black/40 border border-amber-500/30 text-white font-medium focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                            >
                                {ranks.map((rank, index) => (
                                    <option key={rank.id || index} value={index} className="bg-gray-900">
                                        {rank.rank_name} {rank.is_max ? '(MAX)' : ''}
                                    </option>
                                ))}
                            </select>

                            {selectedRank && selectedRank.fields && selectedRank.fields.length > 0 && (
                                <div className={cn('p-4 rounded-xl border', getRankStyle(selectedRank.rank_name))}>
                                    <div className="space-y-2">
                                        {selectedRank.fields.map((field, idx) => (
                                            <div key={idx} className="flex justify-between text-sm">
                                                <span className="text-gray-400">{field.name}</span>
                                                <span className="font-mono font-medium">{field.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Beschreibung */}
            {entry.description && (
                <div className="pt-4 border-t border-white/10">
                    <p className="text-gray-400 text-lg leading-relaxed">
                        {entry.description}
                    </p>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                GACHA: Werte nach Seltenheit
            ════════════════════════════════════════════════════════════════ */}
            {isGacha && entry.values_by_rarity && entry.values_by_rarity.length > 0 && (
                <div className="space-y-4">
                    <h2 className="text-xl font-['Rajdhani'] font-semibold text-purple-400">
                        Werte nach Seltenheit
                    </h2>
                    <div className="space-y-4">
                        {entry.values_by_rarity.filter(v => v.fields?.length > 0).map((rarityData, idx) => {
                            const style = RARITY_STYLES[rarityData.rarity] || RARITY_STYLES.Common;
                            return (
                                <div
                                    key={idx}
                                    className={cn('p-4 rounded-xl border', style.bg, style.border)}
                                >
                                    <div className="flex items-center gap-2">
                                        <span className={cn('font-semibold text-lg', style.text)}>
                                            {rarityData.rarity}
                                        </span>
                                        {rarityData.is_meta && (
                                            <span className="px-2 py-0.5 rounded text-xs font-bold uppercase bg-orange-500 text-black">
                                                META
                                            </span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                                        {rarityData.fields.map((field, fieldIdx) => (
                                            <div key={fieldIdx} className="p-3 rounded bg-black/30">
                                                <p className="text-xs text-gray-500 font-mono">{field.name}</p>
                                                <p className="text-cyan-300 font-mono font-medium">{field.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                UPGRADE: Seltenheiten → Varianten → Stufen-Dropdown
            ════════════════════════════════════════════════════════════════ */}
            {isUpgrade && upgradeRarities.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <h2 className="text-xl font-['Rajdhani'] font-semibold text-green-400">
                            Upgrade-Varianten
                        </h2>
                        <span className="text-xs text-gray-500 font-mono">
                            Wähle pro Variante deine aktuelle Stufe
                        </span>
                    </div>

                    <div className="space-y-4">
                        {upgradeRarities.map((rarityEntry, idx) => (
                            <UpgradeRaritySection key={idx} rarityEntry={rarityEntry} />
                        ))}
                    </div>
                </div>
            )}

            {/* Tutorial Steps */}
            {entry.tutorial_steps && entry.tutorial_steps.length > 0 && (
                <div className="space-y-6">
                    <h2 className="text-xl font-['Rajdhani'] font-semibold text-green-400">
                        Schritte
                    </h2>
                    <div className="space-y-6">
                        {entry.tutorial_steps.map((step, idx) => (
                            <div key={idx} className="flex gap-4">
                                <div className="shrink-0 w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 font-mono font-bold">
                                    {idx + 1}
                                </div>
                                <div className="flex-1 space-y-3">
                                    {step.title && (
                                        <h3 className="text-lg font-['Rajdhani'] font-semibold text-white">
                                            {step.title}
                                        </h3>
                                    )}
                                    {step.content && (
                                        <p className="text-gray-400">{step.content}</p>
                                    )}
                                    {step.video_url && (
                                        <div className="aspect-video rounded-lg overflow-hidden border border-white/10 bg-black">
                                            {step.video_url.includes('youtube.com') || step.video_url.includes('youtu.be') ? (
                                                <iframe
                                                    src={step.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                                                    title={step.title || `Video ${idx + 1}`}
                                                    className="w-full h-full"
                                                    frameBorder="0"
                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                    allowFullScreen
                                                />
                                            ) : step.video_url.includes('vimeo.com') ? (
                                                <iframe
                                                    src={step.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                                                    title={step.title || `Video ${idx + 1}`}
                                                    className="w-full h-full"
                                                    frameBorder="0"
                                                    allow="autoplay; fullscreen; picture-in-picture"
                                                    allowFullScreen
                                                />
                                            ) : (
                                                <video src={step.video_url} controls className="w-full h-full">
                                                    Dein Browser unterstützt das Video-Format nicht.
                                                </video>
                                            )}
                                        </div>
                                    )}
                                    {step.image_url && (
                                        <img
                                            src={step.image_url}
                                            alt={step.title || `Schritt ${idx + 1}`}
                                            className="max-w-full rounded-lg border border-white/10"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Report Modal */}
            <ReportModal
                open={reportModalOpen}
                onOpenChange={setReportModalOpen}
                entry={{ ...entry, title }}
                category={category}
            />
        </div>
    );
};
